import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IndianRupee,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Download,
  Filter,
  Search,
  ChevronDown,
  Calendar,
  Wallet,
  Phone,
  Mail,
  MessageCircle,
  ExternalLink,
  ArrowUpDown
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { api } from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useSearchParams } from 'react-router-dom';
import {
  FinanceDashboardData,
  WonDealWithPayments,
  PaymentStatus,
  PAYMENT_STATUS_COLORS,
  PAYMENT_STATUS_LABELS,
  AgingBucket,
  ArAgingBuckets,
  MonthlyTrendPoint,
  MonthlyTrendResponse
} from '@/types/payment';
import { Lead } from '@/types/lead';
import { LeadDetailPanel } from '@/components/LeadDetailPanel';
import { PeriodFilter, PeriodRange, defaultPeriod } from '@/components/ui/PeriodFilter';
import { extractArray } from '@/lib/deliverablesResponse';

type TabType = 'all' | 'upcoming' | 'overdue';
type AgingFilter = 'all' | AgingBucket;
type SortMode = 'priority' | 'days' | 'balance' | 'won';

const ITEMS_PER_PAGE = 20;

/* ------------------------------------------------------------------ */
/*  Small presentational helpers                                      */
/* ------------------------------------------------------------------ */

const PaymentStatusBadge: React.FC<{ status: PaymentStatus }> = ({ status }) => {
  const colors = PAYMENT_STATUS_COLORS[status];
  return (
    <Badge className={`${colors.bg} ${colors.text} border-0`}>
      <span className={`w-2 h-2 rounded-full ${colors.dot} mr-1.5`} />
      {PAYMENT_STATUS_LABELS[status]}
    </Badge>
  );
};

const SummaryCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}> = ({ title, value, subtitle, icon, color, onClick }) => (
  <Card
    onClick={onClick}
    className={`p-5 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
        <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
        {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
      </div>
      <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-sm`}>
        {icon}
      </div>
    </div>
  </Card>
);

/** Days-pending pill with bucket-based colour */
const DaysPendingPill: React.FC<{ days?: number; bucket?: AgingBucket }> = ({ days, bucket }) => {
  if (bucket === 'paid' || (days === undefined)) {
    return <span className="text-gray-400">—</span>;
  }
  let cls = 'bg-gray-100 text-gray-700';
  if (days >= 61) cls = 'bg-red-100 text-red-700 font-semibold';
  else if (days >= 31) cls = 'bg-orange-100 text-orange-700';
  else if (days >= 8) cls = 'bg-yellow-100 text-yellow-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${cls}`}>
      {days}d
    </span>
  );
};

const Initial: React.FC<{ name: string }> = ({ name }) => {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <span className="h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center shrink-0">
      {initial}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Currency formatter                                                */
/* ------------------------------------------------------------------ */

const formatCurrency = (amount: number): string => {
  if (!amount && amount !== 0) return '₹0';
  if (Math.abs(amount) >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
  if (Math.abs(amount) >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
  if (Math.abs(amount) >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${Math.round(amount).toLocaleString('en-IN')}`;
};

const formatShortDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch {
    return '—';
  }
};

/* ------------------------------------------------------------------ */
/*  Aging helpers                                                      */
/* ------------------------------------------------------------------ */

const BUCKET_ORDER: AgingBucket[] = ['0-7', '8-30', '31-60', '61-90', '91+'];

const computeBucketFromDays = (days?: number, pending?: number): AgingBucket => {
  if (!pending || pending <= 0) return 'paid';
  if (days === undefined) return '0-7';
  if (days < 8) return '0-7';
  if (days < 31) return '8-30';
  if (days < 61) return '31-60';
  if (days < 91) return '61-90';
  return '91+';
};

const emptyBuckets: ArAgingBuckets = {
  '0-7': { count: 0, amount: 0 },
  '8-30': { count: 0, amount: 0 },
  '31-60': { count: 0, amount: 0 },
  '61-90': { count: 0, amount: 0 },
  '91+': { count: 0, amount: 0 }
};

const deriveBucketsFromDeals = (deals: WonDealWithPayments[]): ArAgingBuckets => {
  const buckets: ArAgingBuckets = {
    '0-7': { count: 0, amount: 0 },
    '8-30': { count: 0, amount: 0 },
    '31-60': { count: 0, amount: 0 },
    '61-90': { count: 0, amount: 0 },
    '91+': { count: 0, amount: 0 }
  };
  deals.forEach((d) => {
    const bucket = d.agingBucket ?? computeBucketFromDays(d.daysPending, d.pendingAmount);
    if (bucket === 'paid') return;
    buckets[bucket].count += 1;
    buckets[bucket].amount += d.pendingAmount || 0;
  });
  return buckets;
};

/* ------------------------------------------------------------------ */
/*  Main page                                                         */
/* ------------------------------------------------------------------ */

export const FinanceDashboardPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const initialTab = (searchParams.get('tab') as TabType) || 'all';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [period, setPeriod] = useState<PeriodRange>(defaultPeriod());
  const [filters, setFilters] = useState<{
    paymentStatus?: string;
    assignedTo?: string;
    dateFrom?: string;
    dateTo?: string;
  }>({ dateFrom: defaultPeriod().dateFrom, dateTo: defaultPeriod().dateTo });
  const [showFilters, setShowFilters] = useState(false);
  const [agingFilter, setAgingFilter] = useState<AgingFilter>('all');
  const [sortMode, setSortMode] = useState<SortMode>('priority');
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Read URL params on mount
  useEffect(() => {
    const urlDateFrom = searchParams.get('dateFrom');
    const urlDateTo = searchParams.get('dateTo');
    const urlPaymentStatus = searchParams.get('paymentStatus');
    const urlBucket = searchParams.get('bucket');
    if (urlDateFrom && urlDateTo) {
      setPeriod({ preset: 'custom', dateFrom: urlDateFrom, dateTo: urlDateTo });
      setFilters((prev: any) => ({ ...prev, dateFrom: urlDateFrom, dateTo: urlDateTo }));
    }
    if (urlPaymentStatus) {
      setFilters((prev: any) => ({ ...prev, paymentStatus: urlPaymentStatus }));
    }
    if (urlBucket && (BUCKET_ORDER as string[]).includes(urlBucket)) {
      setAgingFilter(urlBucket as AgingBucket);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLeadClick = (deal: WonDealWithPayments) => {
    const now = new Date().toISOString();
    const wonDate = deal.wonAt || deal.wonDate || now;
    const leadData: Lead = {
      id: deal.id,
      name: deal.name,
      company: deal.company,
      email: deal.email || '',
      phoneNumber: deal.phoneNumber || '',
      dealValue: deal.dealValue,
      stage: 'WON' as const,
      priority: 'HOT' as const,
      paymentStatus: deal.paymentStatus,
      totalPaid: deal.totalPaid,
      nextPaymentDate: deal.nextPaymentDate,
      nextPaymentAmount: deal.nextPaymentAmount,
      createdAt: wonDate,
      lastActivity: wonDate,
      stageUpdatedAt: wonDate,
      updatedAt: wonDate
    };
    setSelectedLead(leadData);
    setIsDetailPanelOpen(true);
  };

  const handleLeadUpdate = (updatedLead: Lead) => {
    queryClient.invalidateQueries({ queryKey: ['finance-dashboard'] });
    setSelectedLead(updatedLead);
  };

  const { data: dashboardData, isLoading, error, isFetching } = useQuery<FinanceDashboardData>({
    queryKey: ['finance-dashboard', filters],
    queryFn: () => api.getFinanceDashboard(filters)
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers()
  });

  const { data: monthlyTrendData } = useQuery<MonthlyTrendPoint[]>({
    queryKey: ['finance-monthly-trend', 6],
    queryFn: async () => {
      const resp: MonthlyTrendResponse = await api.getMonthlyTrend({ months: 6 });
      return resp?.data?.months ?? [];
    },
    staleTime: 60000
  });

  const { data: lifetimeData } = useQuery({
    queryKey: ['lifetime-business'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/lead-metrics/lifetime-business`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) return null;
      const r = await resp.json();
      return r.data;
    },
    staleTime: 60000
  });

  const handleExport = async () => {
    setExportError(null);
    try {
      const blob = await api.exportFinanceData('csv');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `finance-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setExportError('Failed to export data. Please try again.');
    }
  };

  /* --------------------------- derived data --------------------------- */

  const summary = dashboardData?.summary || {
    totalWonDeals: 0,
    totalDealValue: 0,
    totalReceived: 0,
    totalPending: 0,
    noPaymentCount: 0,
    partialPaymentCount: 0,
    fullPaymentCount: 0
  };

  const allDeals: WonDealWithPayments[] = extractArray<WonDealWithPayments>(dashboardData, 'deals');

  // Normalize: ensure every deal has a bucket + daysPending for client-side logic.
  const normalizedDeals = useMemo(
    () =>
      allDeals.map((d) => {
        const bucket = d.agingBucket ?? computeBucketFromDays(d.daysPending, d.pendingAmount);
        return { ...d, agingBucket: bucket };
      }),
    [allDeals]
  );

  // Buckets (prefer backend summary, fall back to client-derived)
  const buckets: ArAgingBuckets = useMemo(() => {
    if (summary.arAging) return { ...emptyBuckets, ...summary.arAging };
    return deriveBucketsFromDeals(normalizedDeals);
  }, [summary.arAging, normalizedDeals]);

  const totalOutstanding =
    summary.totalOutstanding ??
    normalizedDeals.reduce((s, d) => s + (d.pendingAmount || 0), 0);

  const openDealsCount = normalizedDeals.filter((d) => (d.pendingAmount || 0) > 0).length;

  const atRisk60Plus = summary.atRisk60Plus ?? {
    count: buckets['61-90'].count + buckets['91+'].count,
    amount: buckets['61-90'].amount + buckets['91+'].amount
  };

  const collectionRate = Math.min(
    100,
    summary.collectionRate ??
      (summary.totalDealValue > 0
        ? Math.round((summary.totalReceived / summary.totalDealValue) * 100)
        : 0)
  );

  const avgDaysToPay = summary.avgDaysToPay ?? 0;

  /* ------------------------- filter / sort deals ------------------------- */

  const tabFilteredDeals = useMemo(() => {
    if (activeTab === 'upcoming') {
      // treat 0-7 & 8-30 buckets with outstanding balance as "upcoming"
      return normalizedDeals.filter(
        (d) => (d.pendingAmount || 0) > 0 && (d.agingBucket === '0-7' || d.agingBucket === '8-30')
      );
    }
    if (activeTab === 'overdue') {
      // 60+ days overdue
      return normalizedDeals.filter(
        (d) => d.agingBucket === '61-90' || d.agingBucket === '91+'
      );
    }
    return normalizedDeals;
  }, [normalizedDeals, activeTab]);

  const filteredDeals = useMemo(() => {
    let list = tabFilteredDeals;

    if (activeTab === 'all' && agingFilter !== 'all') {
      list = list.filter((d) => d.agingBucket === agingFilter);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          d.company.toLowerCase().includes(q) ||
          (d.assignedTo?.name?.toLowerCase().includes(q) ?? false) ||
          (d.displayId?.toLowerCase().includes(q) ?? false)
      );
    }

    const sorted = [...list];
    // Helper: paid / null daysPending should sink to the bottom regardless of
    // sort direction (days pending = "—" for paid rows, shouldn't float to top).
    const isPaidOrNoDays = (d: WonDealWithPayments) =>
      d.agingBucket === 'paid' ||
      (d.pendingAmount || 0) <= 0 ||
      d.daysPending === undefined ||
      d.daysPending === null;

    sorted.sort((a, b) => {
      switch (sortMode) {
        case 'days': {
          const aPaid = isPaidOrNoDays(a);
          const bPaid = isPaidOrNoDays(b);
          if (aPaid && !bPaid) return 1;   // a sinks
          if (!aPaid && bPaid) return -1;  // b sinks
          if (aPaid && bPaid) return 0;
          return (b.daysPending ?? 0) - (a.daysPending ?? 0);
        }
        case 'balance':
          return (b.pendingAmount || 0) - (a.pendingAmount || 0);
        case 'won':
          return (
            new Date(b.wonAt || b.wonDate).getTime() -
            new Date(a.wonAt || a.wonDate).getTime()
          );
        case 'priority':
        default:
          return (b.agingPriorityScore ?? b.daysPending ?? 0) - (a.agingPriorityScore ?? a.daysPending ?? 0);
      }
    });
    return sorted;
  }, [tabFilteredDeals, agingFilter, searchQuery, sortMode, activeTab]);

  const totalPages = Math.ceil(filteredDeals.length / ITEMS_PER_PAGE) || 1;
  const paginatedDeals = filteredDeals.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters, activeTab, agingFilter, sortMode]);

  /* ----------------------------- loading / error ----------------------------- */

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
          <div className="h-96 bg-gray-100 rounded-xl animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card className="p-8 text-center" role="alert">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load finance data</h2>
            <p className="text-gray-600 mb-4">Please try again later or contact support.</p>
            <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['finance-dashboard'] })}>
              Retry
            </Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  /* --------------------------------- render --------------------------------- */

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Finance Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">
              Accounts receivable, payment collection &amp; aging
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isFetching && <span className="text-xs text-gray-500">Refreshing…</span>}
            <Button variant="outline" size="sm" onClick={handleExport} className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>
        {exportError && (
          <Card className="p-3 border-red-200 bg-red-50 text-sm text-red-700">{exportError}</Card>
        )}

        {/* Lifetime business context (kept from old page) */}
        {lifetimeData && (
          <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500">Lifetime Revenue</p>
                <p className="text-lg font-semibold text-green-700">
                  {formatCurrency(lifetimeData.lifetimeReceived)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Lifetime Pending</p>
                <p className="text-lg font-semibold text-orange-600">
                  {formatCurrency(lifetimeData.lifetimePending)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Collection Rate</p>
                <p className="text-lg font-semibold text-gray-900">{Math.min(100, Math.round(lifetimeData.collectionRate || 0))}%</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Won</p>
                <p className="text-lg font-semibold text-gray-900">
                  {lifetimeData.wonDealsCount} deals
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* AR AGING KPI STRIP */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Outstanding AR"
            value={formatCurrency(totalOutstanding)}
            subtitle={`${openDealsCount} open deal${openDealsCount === 1 ? '' : 's'}`}
            icon={<Wallet className="h-5 w-5 text-white" />}
            color="from-indigo-500 to-indigo-600"
          />
          <Card className={`p-5 ${atRisk60Plus.count > 0 ? 'border-red-200' : ''}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  At Risk (60+ days)
                </p>
                <p
                  className={`mt-2 text-2xl font-semibold ${
                    atRisk60Plus.count > 0 ? 'text-red-600' : 'text-gray-900'
                  }`}
                >
                  {atRisk60Plus.count}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {formatCurrency(atRisk60Plus.amount)} at risk
                </p>
              </div>
              <div
                className={`h-11 w-11 rounded-xl flex items-center justify-center shadow-sm ${
                  atRisk60Plus.count > 0
                    ? 'bg-gradient-to-br from-red-500 to-red-600'
                    : 'bg-gradient-to-br from-gray-300 to-gray-400'
                }`}
              >
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
            </div>
          </Card>
          <SummaryCard
            title="Collection Rate"
            value={`${collectionRate}%`}
            subtitle={`${formatCurrency(summary.totalReceived)} received`}
            icon={<TrendingUp className="h-5 w-5 text-white" />}
            color="from-green-500 to-green-600"
          />
          <SummaryCard
            title="Avg Days to Pay"
            value={avgDaysToPay ? `${Math.round(avgDaysToPay)}d` : '—'}
            subtitle="target: 30d"
            icon={<Clock className="h-5 w-5 text-white" />}
            color="from-amber-500 to-amber-600"
          />
        </div>

        {/* Legacy Summary Cards — kept below AR strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Won Deals"
            value={summary.totalWonDeals}
            subtitle={`${summary.fullPaymentCount} fully paid`}
            icon={<CheckCircle2 className="h-5 w-5 text-white" />}
            color="from-blue-500 to-blue-600"
            onClick={() => {
              setFilters((f) => ({ ...f, paymentStatus: undefined }));
              setActiveTab('all');
            }}
          />
          <SummaryCard
            title="Total Deal Value"
            value={formatCurrency(summary.totalDealValue)}
            subtitle="All won deals"
            icon={<IndianRupee className="h-5 w-5 text-white" />}
            color="from-purple-500 to-purple-600"
          />
          <SummaryCard
            title="Total Received"
            value={formatCurrency(summary.totalReceived)}
            subtitle={`${collectionRate}% collected`}
            icon={<TrendingUp className="h-5 w-5 text-white" />}
            color="from-green-500 to-green-600"
          />
          <SummaryCard
            title="Pending Amount"
            value={formatCurrency(summary.totalPending)}
            subtitle={`${summary.noPaymentCount + summary.partialPaymentCount} deals pending`}
            icon={<Clock className="h-5 w-5 text-white" />}
            color="from-orange-500 to-orange-600"
          />
        </div>

        {/* Aging bucket chip filter */}
        <Card className="p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mr-1">
              Aging:
            </span>
            <button
              aria-pressed={agingFilter === 'all' && activeTab === 'all'}
              onClick={() => {
                setAgingFilter('all');
                setActiveTab('all');
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                agingFilter === 'all' && activeTab === 'all'
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              All
              <span
                className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                  agingFilter === 'all' && activeTab === 'all'
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {normalizedDeals.length}
              </span>
            </button>
            {BUCKET_ORDER.map((b) => {
              const bucket = buckets[b];
              const isActive = agingFilter === b && activeTab === 'all';
              const is91 = b === '91+';
              const is60plus = b === '61-90' || b === '91+';
              return (
                <button
                  key={b}
                  aria-pressed={isActive}
                  onClick={() => {
                    setAgingFilter(b);
                    setActiveTab('all');
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    isActive
                      ? is60plus
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-gray-900 text-white border-gray-900'
                      : is91
                      ? 'bg-white text-red-600 border-red-200 hover:bg-red-50'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {b === '91+' ? '91+d' : `${b}d`}
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                      isActive
                        ? 'bg-white/20 text-white'
                        : is91
                        ? 'bg-red-50 text-red-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {bucket?.count ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Monthly Trend — Booked vs Collected (6-month rolling) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Revenue Booked vs Cash Collected
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Last 6 months · IST calendar
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm" style={{ background: '#93c5fd' }} />
                  <span className="text-gray-600">Booked</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm" style={{ background: '#22c55e' }} />
                  <span className="text-gray-600">Collected</span>
                </div>
              </div>
            </div>
            <div style={{ width: '100%', height: 260 }}>
              {monthlyTrendData && monthlyTrendData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={monthlyTrendData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                    barGap={4}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      axisLine={{ stroke: '#e5e7eb' }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      axisLine={{ stroke: '#e5e7eb' }}
                      tickLine={false}
                      tickFormatter={(v) => {
                        if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
                        if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
                        if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
                        return `₹${v}`;
                      }}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        formatCurrency(value),
                        name === 'booked' ? 'Booked' : 'Collected'
                      ]}
                      labelStyle={{ fontSize: 12, color: '#111827' }}
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
                      }}
                    />
                    <Bar dataKey="booked" fill="#93c5fd" radius={[4, 4, 0, 0]} name="booked" />
                    <Bar dataKey="collected" fill="#22c55e" radius={[4, 4, 0, 0]} name="collected" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-gray-400">
                  No trend data available
                </div>
              )}
            </div>
          </Card>

          {/* Monthly rate table */}
          <Card className="p-5">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Monthly Collection Rate</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Rate = Collected / Booked · color coded
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-200">
                    <th className="py-2 pr-3">Month</th>
                    <th className="py-2 pr-3 text-right">Booked</th>
                    <th className="py-2 pr-3 text-right">Collected</th>
                    <th className="py-2 text-right">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(monthlyTrendData ?? []).map((m) => {
                    const rateColor =
                      m.rate >= 100
                        ? 'bg-green-50 text-green-700'
                        : m.rate >= 30
                        ? 'bg-orange-50 text-orange-700'
                        : 'bg-red-50 text-red-700';
                    return (
                      <tr key={m.month} className="border-b border-gray-100 last:border-b-0">
                        <td className="py-2 pr-3 text-gray-900">{m.label}</td>
                        <td className="py-2 pr-3 text-right text-gray-700">
                          {formatCurrency(m.booked)}
                        </td>
                        <td className="py-2 pr-3 text-right text-gray-700">
                          {formatCurrency(m.collected)}
                        </td>
                        <td className="py-2 text-right">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${rateColor}`}
                          >
                            {m.rate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {(!monthlyTrendData || monthlyTrendData.length === 0) && (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-xs text-gray-400">
                        No data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* AR by Executive — overdue rollup (60+ days) */}
        <Card className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">AR by Executive</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Overdue deals (60+ days pending) per assignee
              </p>
            </div>
          </div>
          {summary.arByExecutive && summary.arByExecutive.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-3 font-medium">Executive</th>
                    <th className="py-2 pr-3 font-medium text-right">Overdue Deals</th>
                    <th className="py-2 pr-3 font-medium text-right">Pending Amount</th>
                    <th className="py-2 pr-3 font-medium text-right">Oldest (days)</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.arByExecutive.map((exec) => (
                    <tr
                      key={exec.userId || '__unassigned__'}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <td className="py-2 pr-3 text-gray-900">{exec.userName}</td>
                      <td className="py-2 pr-3 text-right text-gray-700">{exec.count}</td>
                      <td className="py-2 pr-3 text-right font-medium text-gray-900">
                        {formatCurrency(exec.amount)}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            exec.oldestDays >= 91
                              ? 'bg-red-100 text-red-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}
                        >
                          {exec.oldestDays}d
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-6 text-center text-xs text-gray-400">
              No overdue AR by executive
            </div>
          )}
        </Card>

        {/* Tabs / Search / Filter / Sort */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={activeTab === 'all' ? 'default' : 'outline'}
              onClick={() => {
                setActiveTab('all');
              }}
              size="sm"
            >
              All Deals ({normalizedDeals.length})
            </Button>
            <Button
              variant={activeTab === 'upcoming' ? 'default' : 'outline'}
              onClick={() => setActiveTab('upcoming')}
              size="sm"
            >
              Pending AR ({buckets['0-7'].count + buckets['8-30'].count})
            </Button>
            <Button
              variant={activeTab === 'overdue' ? 'default' : 'outline'}
              onClick={() => {
                setActiveTab('overdue');
                setAgingFilter('all');
              }}
              size="sm"
              className={
                activeTab !== 'overdue' && atRisk60Plus.count > 0
                  ? 'border-red-300 text-red-600'
                  : ''
              }
            >
              60+ days overdue ({atRisk60Plus.count})
            </Button>
          </div>

          <div className="flex gap-2 w-full lg:w-auto items-center">
            <div className="relative flex-1 lg:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search name, ID or assignee…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                aria-label="Search won deals"
              />
            </div>

            {/* Sort control */}
            <div className="flex items-center gap-1 rounded-xl border-2 border-gray-200 bg-white/50 px-2 py-1">
              <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs text-gray-500 hidden sm:inline">Sort:</span>
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
                className="text-xs bg-transparent border-0 focus:ring-0 focus:outline-none text-gray-900 cursor-pointer"
                aria-label="Sort deals"
              >
                <option value="priority">Priority</option>
                <option value="days">Days pending</option>
                <option value="balance">Balance desc</option>
                <option value="won">Won date</option>
              </select>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">Filters</span>
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`}
              />
            </Button>
          </div>
        </div>

        {/* Period filter */}
        <PeriodFilter
          label="Deal Won Period:"
          value={period}
          onChange={(range) => {
            setPeriod(range);
            setFilters((f) => ({ ...f, dateFrom: range.dateFrom, dateTo: range.dateTo }));
          }}
          className="flex-row items-center gap-3"
        />

        {/* Assignee filter */}
        {showFilters && (
          <Card className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Assigned to:</span>
              <select
                value={filters.assignedTo || ''}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, assignedTo: e.target.value || undefined }))
                }
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm bg-white"
              >
                <option value="">All</option>
                {(Array.isArray(users)
                  ? users
                  : (users as any)?.users || []
                ).map((u: any) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </Card>
        )}

        {/* =================== MAIN TABLE =================== */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto w-full">
            <table className="w-full min-w-[900px] text-sm" role="table" aria-label="AR aging table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  <th scope="col" className="px-4 py-3">Name / ID</th>
                  <th scope="col" className="px-4 py-3">Plan</th>
                  <th scope="col" className="px-4 py-3">Assigned</th>
                  <th scope="col" className="px-4 py-3">Won Date</th>
                  <th scope="col" className="px-4 py-3 text-right">Full Value</th>
                  <th scope="col" className="px-4 py-3 text-right">Received</th>
                  <th scope="col" className="px-4 py-3 text-right">Balance</th>
                  <th scope="col" className="px-4 py-3">Days Pending</th>
                  <th scope="col" className="px-4 py-3">Status</th>
                  <th scope="col" className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedDeals.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-16 text-center">
                      <Wallet className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 font-medium">No deals match this filter</p>
                      <p className="text-gray-400 text-xs mt-1">
                        Try clearing the aging filter or changing the period.
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedDeals.map((deal) => {
                    const hasPhone = !!deal.phoneNumber;
                    const hasEmail = !!deal.email;
                    const balance = deal.pendingAmount || 0;
                    const received = deal.totalPaid || 0;
                    const unassigned = !deal.assignedTo;

                    const waHref = hasPhone
                      ? `https://wa.me/${(deal.phoneNumber || '').replace(/\D/g, '')}?text=${encodeURIComponent(
                          `Hi ${deal.name}`
                        )}`
                      : undefined;
                    const leadUrl = `/leads/${deal.id}`;

                    return (
                      <tr
                        key={deal.id}
                        onClick={() => handleLeadClick(deal)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        {/* Name + ID */}
                        <td className="px-4 py-3 align-middle">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{deal.name}</span>
                            <span className="text-[11px] text-gray-500">
                              {deal.displayId ? deal.displayId : deal.company}
                            </span>
                          </div>
                        </td>

                        {/* Plan */}
                        <td className="px-4 py-3 align-middle">
                          {deal.subscription?.planName ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                              {deal.subscription.planName}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>

                        {/* Assigned */}
                        <td className="px-4 py-3 align-middle">
                          {unassigned ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-red-50 text-red-700 border border-red-100">
                              Unassigned
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Initial name={deal.assignedTo!.name} />
                              <span className="text-gray-700 truncate max-w-[140px]">
                                {deal.assignedTo!.name}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Won Date */}
                        <td className="px-4 py-3 align-middle text-gray-700">
                          {formatShortDate(deal.wonAt || deal.wonDate)}
                        </td>

                        {/* Full Value */}
                        <td className="px-4 py-3 align-middle text-right text-gray-900 font-medium tabular-nums">
                          {formatCurrency(deal.dealValue)}
                        </td>

                        {/* Received */}
                        <td
                          className={`px-4 py-3 align-middle text-right tabular-nums ${
                            received > 0 ? 'text-green-600 font-medium' : 'text-gray-400'
                          }`}
                        >
                          {formatCurrency(received)}
                        </td>

                        {/* Balance */}
                        <td
                          className={`px-4 py-3 align-middle text-right tabular-nums ${
                            balance > 0 ? 'text-red-600 font-semibold' : 'text-green-600'
                          }`}
                        >
                          {formatCurrency(balance)}
                        </td>

                        {/* Days pending */}
                        <td className="px-4 py-3 align-middle">
                          <DaysPendingPill days={deal.daysPending} bucket={deal.agingBucket} />
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 align-middle">
                          <PaymentStatusBadge status={deal.paymentStatus} />
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center justify-center gap-0.5">
                            {hasPhone ? (
                              <a
                                href={`tel:+${(deal.phoneNumber || '').replace(/\D/g, '')}`}
                                onClick={(e) => e.stopPropagation()}
                                title={`Call ${deal.phoneNumber}`}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                                aria-label="Call"
                              >
                                <Phone className="h-4 w-4" />
                              </a>
                            ) : (
                              <span
                                title="No phone on file"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-300 cursor-not-allowed"
                              >
                                <Phone className="h-4 w-4" />
                              </span>
                            )}

                            {hasEmail ? (
                              <a
                                href={`mailto:${deal.email}`}
                                onClick={(e) => e.stopPropagation()}
                                title={`Email ${deal.email}`}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                                aria-label="Email"
                              >
                                <Mail className="h-4 w-4" />
                              </a>
                            ) : (
                              <span
                                title="No email on file"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-300 cursor-not-allowed"
                              >
                                <Mail className="h-4 w-4" />
                              </span>
                            )}

                            {waHref ? (
                              <a
                                href={waHref}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title="Open WhatsApp chat"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-600 hover:text-green-600 hover:bg-green-50 transition-colors"
                                aria-label="WhatsApp"
                              >
                                <MessageCircle className="h-4 w-4" />
                              </a>
                            ) : (
                              <span
                                title="No phone on file"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-300 cursor-not-allowed"
                              >
                                <MessageCircle className="h-4 w-4" />
                              </span>
                            )}

                            <a
                              href={leadUrl}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title="Open lead in new tab"
                              className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                              aria-label="Open lead"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
              <span className="text-xs text-gray-500">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredDeals.length)} of{' '}
                {filteredDeals.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-xs font-medium border border-gray-200 rounded-md hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-500 px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-xs font-medium border border-gray-200 rounded-md hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </Card>

        {/* Optional: keep a lightweight Upcoming / Overdue summary beneath table */}
        {activeTab === 'upcoming' && dashboardData?.upcomingPayments?.length ? (
          <Card className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-600" />
              Scheduled milestones (next 30 days)
            </h3>
            <div className="space-y-2">
              {dashboardData.upcomingPayments.slice(0, 5).map((p: any) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{p.title}</p>
                    <p className="text-xs text-gray-500">
                      {p.leadName} · {p.company}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(p.amount)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Due {new Date(p.dueDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        <LeadDetailPanel
          lead={selectedLead}
          isOpen={isDetailPanelOpen}
          onClose={() => {
            setIsDetailPanelOpen(false);
            setSelectedLead(null);
            queryClient.invalidateQueries({ queryKey: ['finance-dashboard'] });
          }}
          onLeadUpdate={handleLeadUpdate}
          initialTab="payments"
        />
      </div>
    </DashboardLayout>
  );
};

export default FinanceDashboardPage;
